<script setup>
import { computed, nextTick, onMounted, ref } from "vue";
import { useStore } from "vuex";

// Marks the parts of a picture to redraw: circle them (or paint over them) with a
// finger or the mouse, then say what should go there. Only the marked parts change.
defineProps({
  src: { type: String, required: true },
  alt: { type: String, default: "" },
});
const emit = defineEmits(["submit", "cancel"]);

const store = useStore();
const cost = computed(() => store.state.wishes.costs?.image ?? 2);

const img = ref(null);
const canvas = ref(null);
const input = ref(null);
const change = ref("");
// Each mark is the list of points it was drawn through, in the picture's own pixels
const marks = ref([]);
let drawing = null;

const size = () => ({
  width: img.value?.naturalWidth || 0,
  height: img.value?.naturalHeight || 0,
});
// Wide enough to cover something by painting over it
const brush = () => Math.max(12, size().width * 0.05);

// A mark is its outline filled in, plus a thick line along it, so a loop covers what
// it goes round and a scribble covers what it goes over
const paint = (ctx, mark) => {
  const [first, ...rest] = mark;
  ctx.beginPath();
  if (!rest.length) {
    ctx.arc(first.x, first.y, brush() / 2, 0, Math.PI * 2);
    ctx.fill();
    return;
  }
  ctx.moveTo(first.x, first.y);
  rest.forEach((point) => ctx.lineTo(point.x, point.y));
  ctx.closePath();
  ctx.fill();
  ctx.lineWidth = brush();
  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  ctx.stroke();
};

const draw = () => {
  const el = canvas.value;
  if (!el) return;
  const ctx = el.getContext("2d");
  ctx.clearRect(0, 0, el.width, el.height);
  ctx.fillStyle = ctx.strokeStyle = "#CF5AE2";
  marks.value.forEach((mark) => paint(ctx, mark));
  if (drawing?.length > 1) {
    // Still being drawn: just the line so far
    ctx.beginPath();
    ctx.moveTo(drawing[0].x, drawing[0].y);
    drawing.slice(1).forEach((point) => ctx.lineTo(point.x, point.y));
    ctx.lineWidth = brush();
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    ctx.stroke();
  }
};

const fit = () => {
  const { width, height } = size();
  if (!width || !canvas.value) return;
  canvas.value.width = width;
  canvas.value.height = height;
  draw();
};
onMounted(async () => {
  await nextTick();
  if (img.value?.complete) fit();
});

const pointAt = (event) => {
  const box = canvas.value.getBoundingClientRect();
  const { width, height } = size();
  return {
    x: ((event.clientX - box.left) / box.width) * width,
    y: ((event.clientY - box.top) / box.height) * height,
  };
};
const start = (event) => {
  if (!size().width) return;
  canvas.value.setPointerCapture?.(event.pointerId);
  drawing = [pointAt(event)];
  draw();
};
const move = (event) => {
  if (!drawing) return;
  const point = pointAt(event);
  const last = drawing[drawing.length - 1];
  if (Math.hypot(point.x - last.x, point.y - last.y) < 3) return;
  drawing.push(point);
  draw();
};
const end = () => {
  if (!drawing) return;
  marks.value = [...marks.value, drawing];
  drawing = null;
  draw();
  input.value?.focus({ preventScroll: true });
};

const undo = () => {
  marks.value = marks.value.slice(0, -1);
  draw();
};
const clear = () => {
  marks.value = [];
  draw();
};

// The image model redraws where the mask is see-through and keeps the rest
const maskUrl = () => {
  const { width, height } = size();
  const mask = document.createElement("canvas");
  mask.width = width;
  mask.height = height;
  const ctx = mask.getContext("2d");
  ctx.fillStyle = "#000";
  ctx.fillRect(0, 0, width, height);
  ctx.globalCompositeOperation = "destination-out";
  ctx.fillStyle = ctx.strokeStyle = "#000";
  marks.value.forEach((mark) => paint(ctx, mark));
  return mask.toDataURL("image/png");
};

const ready = computed(() => marks.value.length > 0 && change.value.trim());
const submit = () => {
  if (!ready.value) return;
  emit("submit", { mask: maskUrl(), change: change.value.trim() });
};
</script>

<template>
  <div class="w-full max-w-xl">
    <div class="relative">
      <img
        ref="img"
        :src="src"
        :alt="alt"
        class="w-full select-none rounded-lg shadow"
        draggable="false"
        @load="fit"
      />
      <canvas
        ref="canvas"
        class="absolute inset-0 h-full w-full cursor-crosshair touch-none rounded-lg opacity-50"
        aria-label="Draw round the parts of the picture to redraw"
        role="img"
        @pointerdown.prevent="start"
        @pointermove="move"
        @pointerup="end"
        @pointercancel="end"
      />
      <p
        v-if="!marks.length"
        class="pointer-events-none absolute inset-x-0 top-3 mx-auto w-max max-w-[90%] rounded-full bg-primary/80 px-3 py-1.5 text-center text-xs font-bold text-white"
      >
        Circle or paint over the parts to redraw
      </p>
    </div>
    <form
      class="mt-3 rounded-2xl bg-primary p-3 text-white"
      @submit.prevent="submit"
    >
      <div class="flex items-center gap-2">
        <label for="picture-marker-change" class="flex-1 text-sm font-bold">
          What should go there?
        </label>
        <button
          type="button"
          class="flex items-center rounded-full px-2 py-1 text-xs font-bold text-secondaryLight hover:text-white disabled:opacity-40"
          :disabled="!marks.length"
          @click="undo"
        >
          <font-awesome-icon :icon="['fas', 'rotate-left']" class="mr-1" />
          Undo
        </button>
        <button
          type="button"
          class="flex items-center rounded-full px-2 py-1 text-xs font-bold text-secondaryLight hover:text-white disabled:opacity-40"
          :disabled="!marks.length"
          @click="clear"
        >
          <font-awesome-icon :icon="['fas', 'trash-can']" class="mr-1" />
          Clear
        </button>
      </div>
      <input
        id="picture-marker-change"
        ref="input"
        v-model="change"
        type="text"
        autocomplete="off"
        enterkeyhint="go"
        placeholder="A red hat, a sunset, no people..."
        maxlength="1000"
        class="mt-2 block w-full rounded-xl border-0 bg-white px-3 py-2.5 text-base text-primary placeholder:text-secondaryLight focus:outline-none focus:ring-2 focus:ring-accentSecondary"
      />
      <div class="mt-2 flex items-center gap-2">
        <p class="mr-auto text-xs text-secondaryLight">
          Only the marked parts change. Uses {{ cost }} wishes.
        </p>
        <button
          type="button"
          class="rounded-full px-3 py-2 text-sm font-bold text-secondaryLight hover:text-white"
          @click="emit('cancel')"
        >
          Cancel
        </button>
        <button
          type="submit"
          class="flex items-center rounded-full bg-accentSecondary px-4 py-2 text-sm font-bold text-primary transition-colors hover:bg-yellow-300 disabled:opacity-40"
          :disabled="!ready"
        >
          <font-awesome-icon :icon="['fas', 'paintbrush']" class="mr-1.5" />
          Redraw
        </button>
      </div>
    </form>
  </div>
</template>
